import { Disclosure } from '@headlessui/react';
import { Role } from 'auth';
import { Dispatch, JSX, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { ArchiveSwitch } from '@/modules/common/components/ArchiveSwitch.tsx';
import '@/modules/common/styles/common.css';
import InvestorActivityFilters from '@/modules/investor-dashboard/components/InvestorActivityFilters.tsx';
import { activityFilters } from '@/modules/investor-dashboard/pages/InvestorActivity.tsx';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import IssuerPaymentsFilters from '@/modules/issuer-dashboard/components/IssuerPaymentsFilters.tsx';
import IssuerTransfersFilters from '@/modules/issuer-dashboard/components/IssuerTransfersFilters.tsx';
import {
  IssuerActivityTab,
  TransactionFilters,
} from '@/modules/issuer-dashboard/components/operation-tabs/IssuerActivityTab.tsx';
import {
  IssuerPaymentsTab,
  PaymentTabFilters,
} from '@/modules/issuer-dashboard/components/operation-tabs/IssuerPaymentsTab.tsx';
import { IssuerTransactionsTab } from '@/modules/issuer-dashboard/components/operation-tabs/IssuerTransactionsTab.tsx';
import {
  IssuerTransfersTab,
  transferTabFilters,
} from '@/modules/issuer-dashboard/components/operation-tabs/IssuerTransfersTab.tsx';
import { useIssuerAddActivityMenuOptions } from '@/modules/issuer-dashboard/hooks/useIssuerAddActivityMenuOptions.ts';
import { Button, DropdownMenu, PageLayout, TabItem, Tabs } from 'ui';
import { FiltersIcon } from 'ui/icons';
import { ActivitiesType } from 'api';

enum TabNames {
  Activity = 'Activity',
  Payments = 'Payments',
  Transfers = 'Transfers',
  Transactions = 'Transactions',
}

type filters = {
  Activity: TransactionFilters;
  Payments: PaymentTabFilters;
  Transfers: transferTabFilters;
  Transactions: TransactionFilters;
};

const OPERATIONS_TAB_NAME = 'tab-operations';

const IssuerOperations = () => {
  const { registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const [isDeleted, setIsDeleted] = useState<boolean | undefined>();
  const [params] = useSearchParams();
  const paramTab = params.get(OPERATIONS_TAB_NAME);
  const isValidTab = paramTab && Object.values(TabNames).includes(paramTab as TabNames);
  const [activeTab, setActiveTab] = useState<TabNames>(
    isValidTab ? (paramTab as TabNames) : TabNames.Activity,
  );

  const [filters, setFilters] = useState<filters>({
    Activity: {},
    Payments: {},
    Transfers: {},
    Transactions: {},
  });

  const tabItems = useMemo<TabItem<TabNames>[]>(
    () => [
      {
        name: TabNames.Activity,
        container: (
          <IssuerActivityTab
            register_id={Number(registerId)}
            filters={{ ...filters.Activity, is_deleted: isDeleted }}
          />
        ),
      },
      {
        name: TabNames.Payments,
        container: (
          <IssuerPaymentsTab
            register_id={Number(registerId)}
            filters={{ ...filters.Payments, is_deleted: isDeleted }}
          />
        ),
      },
      {
        name: TabNames.Transfers,
        container: (
          <IssuerTransfersTab
            register_id={Number(registerId)}
            filters={{ ...filters.Transfers, is_deleted: isDeleted }}
          />
        ),
      },
      {
        name: TabNames.Transactions,
        container: (
          <IssuerTransactionsTab
            register_id={Number(registerId)}
            filters={{ ...filters.Transactions, is_deleted: isDeleted }}
          />
        ),
      },
    ],
    [filters, registerId, isDeleted],
  );

  const handleChangeFilters = useCallback(
    <T extends TransactionFilters | PaymentTabFilters | transferTabFilters>(
      type: TabNames,
      cb: (filters: T) => T,
    ) => {
      setFilters(filters => ({
        ...filters,
        [type]: cb(filters[type] as T),
      }));
    },
    [],
  );

  const mapTabToFiltersComponent: Record<TabNames, JSX.Element> = useMemo(
    () => ({
      Activity: (
        <InvestorActivityFilters
          showStatus={true}
          filters={filters.Activity}
          setFilters={
            handleChangeFilters.bind(null, TabNames.Activity) as (
              cb: (filters: activityFilters) => activityFilters,
            ) => void
          }
        />
      ),
      Payments: (
        <IssuerPaymentsFilters
          filters={filters.Payments}
          setFilters={
            handleChangeFilters.bind(null, TabNames.Payments) as (
              cb: (filters: PaymentTabFilters) => PaymentTabFilters,
            ) => void
          }
        />
      ),
      Transfers: (
        <IssuerTransfersFilters
          filters={filters.Transfers}
          setFilters={
            handleChangeFilters.bind(null, TabNames.Transfers) as (
              cb: (filters: transferTabFilters) => transferTabFilters,
            ) => void
          }
        />
      ),
      Transactions: (
        <InvestorActivityFilters
          showStatus={true}
          filters={filters.Transactions}
          types={Object.values(ActivitiesType).filter(
            type =>
              ![
                ActivitiesType.Transfer,
                ActivitiesType.FundToInvestor,
                ActivitiesType.InvestorToFund,
              ].includes(type),
          )}
          setFilters={
            handleChangeFilters.bind(null, TabNames.Transactions) as (
              cb: (filters: activityFilters) => activityFilters,
            ) => void
          }
        />
      ),
    }),
    [filters, handleChangeFilters],
  );

  return (
    <Disclosure>
      <PageLayout
        title="Operations"
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabNameParam={OPERATIONS_TAB_NAME}
        tabs={tabItems}
        headerAction={<OperationsHeaderAction isDeleted={isDeleted} setIsDeleted={setIsDeleted} />}
        headerBottomContent={mapTabToFiltersComponent[activeTab]}
      >
        <Tabs items={tabItems} activeTab={activeTab} />
      </PageLayout>
    </Disclosure>
  );
};

interface OperationsHeaderActionProps {
  isDeleted?: boolean;
  setIsDeleted?: Dispatch<SetStateAction<boolean | undefined>>;
}

const OperationsHeaderAction = ({ isDeleted, setIsDeleted }: OperationsHeaderActionProps) => {
  const { registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const addActivityOptions = useIssuerAddActivityMenuOptions({
    registerId: Number(registerId),
  });

  return (
    <div className="flex gap-4 items-center">
      {setIsDeleted && (
        <ArchiveSwitch
          active={!!isDeleted}
          onChange={active => setIsDeleted(active || undefined)}
        />
      )}
      <Disclosure.Button as="span">
        <Button icon={FiltersIcon} variant="secondary">
          Filters
        </Button>
      </Disclosure.Button>
      <DropdownMenu
        items={addActivityOptions}
        button={<Button className="w-auto">Add transaction</Button>}
        containerClassName="!w-auto"
        className="w-[160px] divide-white"
        placement="left-start"
      />
    </div>
  );
};

export default withPrivateAccess(IssuerOperations, [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN]);
